import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { generateInventoryReport } from "../utils/pdfGenerator";

/* ─── Local SVG Icons ─────────────────────────────────────────── */
const DogIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 11v1.5a5 5 0 0 0 10 0V11"/>
    <path d="M17 11A5 5 0 0 0 7 11"/>
    <path d="M7 8c-2 0-4 1.5-4 4s1.5 4 2.5 4 2.5-1.5 2.5-3"/>
    <path d="M17 8c2 0 4 1.5 4 4s-1.5 4-2.5 4-2.5-1.5-2.5-3"/>
    <path d="M12 14v1"/><path d="M11 14h2"/>
    <path d="M10 10.5v.5"/><path d="M14 10.5v.5"/>
  </svg>
);

const CatIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5z"/>
    <path d="M8 14v.5"/><path d="M16 14v.5"/>
    <path d="M11.25 16.25h1.5L12 17l-.75-.75z"/>
  </svg>
);

const PawIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="5.2" cy="10.2" rx="2" ry="2.6" transform="rotate(-12 5.2 10.2)" />
    <ellipse cx="9.8" cy="7" rx="2" ry="2.6" transform="rotate(-4 9.8 7)" />
    <ellipse cx="14.2" cy="7" rx="2" ry="2.6" transform="rotate(4 14.2 7)" />
    <ellipse cx="18.8" cy="10.2" rx="2" ry="2.6" transform="rotate(12 18.8 10.2)" />
    <path d="M12 13.5c-4 0-7 2.1-7 4.8 0 2.4 2.2 4 5 4.4.6.1 1.3.1 2 .1s1.4 0 2-.1c2.8-.4 5-2 5-4.4 0-2.7-3-4.8-7-4.8z" />
  </svg>
);

const CoinsIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6" />
    <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
    <path d="M7 6h2v4H7z" />
    <path d="M15 12h2v4h-2z" />
  </svg>
);

const GridIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const TableIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" /><path d="M3 15h18" /><path d="M9 3v18" />
  </svg>
);

const renderServiceIcon = (iconName, size = 20) => {
  if (iconName === "🐕" || iconName === "dog_small") return <DogIcon size={size} color="#6992a5" />;
  if (iconName === "🐩" || iconName === "dog_large") return <DogIcon size={size} color="#46735c" />;
  if (iconName === "🐈" || iconName === "cat_villa") return <CatIcon size={size} color="#e0a458" />;
  if (iconName === "🐾" || iconName === "paw") return <PawIcon size={size} color="#9333EA" />;
  return <span style={{ fontSize: `${size}px` }}>{iconName || "🐾"}</span>;
};

const POPULAR_ICONS = ["🐾", "🐕", "🐩", "🐈", "✂️", "🛁", "🪥", "👁️", "👂", "🩹", "🏨", "👑", "⭐", "🧴"];

const DEFAULT_DOG_SIZES = [
  { label: "Small (Single Coated)", price: 400 },
  { label: "Medium (Single Coated)", price: 500 },
  { label: "Large (Double Coated)", price: 650 },
  { label: "Extra Large (Triple Coated)", price: 850 }
];

/* ─── ServiceCard ─────────────────────────────────────────────── */
const ServiceCard = ({ service, onEditPrice, onEditFull, onDelete }) => {
  const hasSizes = Array.isArray(service.sizes) && service.sizes.length > 0;
  const isHotel = service.type === "hotel";

  return (
    <div className="p-5 sm:p-6 bg-white border border-purple-100/80 rounded-3xl space-y-4 transition-all hover:border-purple-300 hover:shadow-xl shadow-sm flex flex-col justify-between group">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center flex-shrink-0 text-xl border border-purple-100 shadow-inner group-hover:scale-105 transition-transform">
              {renderServiceIcon(service.icon, 24)}
            </div>
            <div>
              <div className="font-black text-gray-800 text-base leading-snug">{service.name}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                  {service.type === "addon" ? "Add-On" : service.type}
                </span>
                {service.category && (
                  <span className="text-[10px] font-bold text-gray-400 uppercase">
                    • {service.category}
                  </span>
                )}
                {isHotel && service.capacity && (
                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                    Cap: {service.capacity} Pets
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed min-h-[32px]">
          {service.description || "No description provided."}
        </p>

        {/* Pricing Display */}
        {hasSizes ? (
          <div className="bg-purple-50/70 p-3 rounded-2xl border border-purple-100 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-black uppercase text-purple-600 tracking-wider">
                Size &amp; Coat Rates
              </span>
              <button
                type="button"
                onClick={() => onEditPrice(service)}
                className="text-[11px] font-bold text-purple-700 hover:text-purple-900 bg-white px-2 py-0.5 rounded-lg border border-purple-200 shadow-2xs flex items-center gap-1 transition-all hover:bg-purple-100"
              >
                <CoinsIcon size={12} color="#7C3AED" /> Edit Rates
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {service.sizes.map((s, idx) => (
                <div
                  key={idx}
                  onClick={() => onEditPrice(service)}
                  className="bg-white/90 px-2.5 py-1.5 rounded-xl border border-purple-100 flex items-center justify-between text-xs cursor-pointer hover:border-purple-300 hover:bg-purple-50 transition-colors"
                  title="Click to edit price"
                >
                  <span className="font-semibold text-gray-700 truncate mr-1 text-[11px]">{s.label}</span>
                  <span className="font-black text-purple-700 text-xs">₱{Number(s.price || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            onClick={() => onEditPrice(service)}
            className="flex items-center justify-between bg-purple-50/60 hover:bg-purple-100/70 cursor-pointer px-4 py-3 rounded-2xl border border-purple-100 transition-all group/price"
            title="Click to edit price"
          >
            <div>
              <span className="text-[11px] font-bold text-gray-500 block">
                {isHotel ? "Rate per Night" : "Service Price"}
              </span>
              <span className="text-[10px] text-purple-500 font-semibold flex items-center gap-1 group-hover/price:underline">
                <CoinsIcon size={11} color="#9333EA" /> Click to edit price
              </span>
            </div>
            <span className="text-lg sm:text-xl font-black text-purple-700 bg-white px-3 py-1 rounded-xl border border-purple-100 shadow-2xs">
              ₱{Number(service.price || 0).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-purple-50 mt-2">
        <button
          type="button"
          onClick={() => onEditPrice(service)}
          className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold text-xs rounded-xl shadow-xs hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-1.5"
        >
          <CoinsIcon size={13} color="#FFFFFF" /> Edit Price
        </button>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onEditFull(service)}
            className="px-3 py-1.5 bg-purple-50 text-purple-700 font-bold text-xs rounded-xl hover:bg-purple-100 transition-all flex items-center gap-1 border border-purple-100"
            title="Edit all service details"
          >
            ✏️ Details
          </button>
          <button
            type="button"
            onClick={() => onDelete(service)}
            className="px-2.5 py-1.5 bg-red-50 text-red-600 font-bold text-xs rounded-xl hover:bg-red-100 transition-all flex items-center gap-1 border border-red-100"
            title="Deactivate service"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── AdminServices Main Component ─────────────────────────────── */
const AdminServices = ({ user }) => {
  const [services, setServices] = useState({ grooming: { dog: [], cat: [], addons: [] }, hotel: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "table"

  // Quick Price Modal
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [priceTargetService, setPriceTargetService] = useState(null);
  const [priceForm, setPriceForm] = useState({ price: 0, sizes: [] });

  // Full Edit Modal
  const [fullEditModalOpen, setFullEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  // Add Service Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newService, setNewService] = useState({
    serviceType: "dog_grooming",
    name: "",
    description: "",
    icon: "🐾",
    price: "",
    capacity: "",
    sizes: DEFAULT_DOG_SIZES.map(s => ({ ...s }))
  });

  const [toastMsg, setToastMsg] = useState("");

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3500);
  };

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get("/api/services");
      if (res.data.success) setServices(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching services:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Open Quick Price Modal
  const handleOpenPriceModal = (service) => {
    setPriceTargetService(service);
    setPriceForm({
      price: Number(service.price) || 0,
      sizes: Array.isArray(service.sizes) && service.sizes.length > 0
        ? service.sizes.map(s => ({ label: s.label, price: Number(s.price) || 0 }))
        : []
    });
    setPriceModalOpen(true);
  };

  // Save Quick Price Modal
  const handleSaveQuickPrice = async (e) => {
    e.preventDefault();
    if (!priceTargetService) return;
    setSubmitting(true);

    try {
      const payload = {
        name: priceTargetService.name,
        type: priceTargetService.type,
        category: priceTargetService.category,
      };

      if (priceForm.sizes.length > 0) {
        payload.sizes = priceForm.sizes.map(s => ({
          label: s.label.trim(),
          price: Math.max(0, Number(s.price) || 0)
        }));
      } else {
        payload.price = Math.max(0, Number(priceForm.price) || 0);
      }

      await axios.put(`/api/services/${priceTargetService._id}`, payload, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });

      setPriceModalOpen(false);
      showToast(`💰 Prices for "${priceTargetService.name}" updated successfully!`);
      fetchServices();
    } catch (err) {
      console.error("Error updating price:", err);
      alert(err.response?.data?.message || "Failed to update price.");
    } finally {
      setSubmitting(false);
    }
  };

  // Open Full Edit Modal
  const handleOpenFullEdit = (service) => {
    setEditForm({
      ...service,
      price: Number(service.price) || 0,
      sizes: Array.isArray(service.sizes) && service.sizes.length > 0
        ? service.sizes.map(s => ({ label: s.label, price: Number(s.price) || 0 }))
        : [],
      capacity: service.capacity ? Number(service.capacity) : 4,
    });
    setFullEditModalOpen(true);
  };

  // Save Full Edit Modal
  const handleSaveFullEdit = async (e) => {
    e.preventDefault();
    if (!editForm) return;
    if (!editForm.name?.trim()) {
      alert("Please provide a service name.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        description: editForm.description?.trim() || "",
        icon: editForm.icon || "🐾",
        type: editForm.type,
        category: editForm.category,
        isActive: editForm.isActive !== false,
      };

      if (editForm.type === "grooming" && editForm.category === "dog" && editForm.sizes?.length > 0) {
        payload.sizes = editForm.sizes.map(s => ({
          label: s.label.trim(),
          price: Math.max(0, Number(s.price) || 0)
        }));
      } else {
        payload.price = Math.max(0, Number(editForm.price) || 0);
        if (editForm.type === "hotel") {
          payload.capacity = Math.max(1, Number(editForm.capacity) || 4);
        }
      }

      await axios.put(`/api/services/${editForm._id}`, payload, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });

      setFullEditModalOpen(false);
      showToast(`✅ "${payload.name}" updated successfully!`);
      fetchServices();
    } catch (err) {
      console.error("Error updating service:", err);
      alert(err.response?.data?.message || "Failed to update service.");
    } finally {
      setSubmitting(false);
    }
  };

  // Open Add Service Modal
  const handleOpenAddModal = (presetType = "dog_grooming") => {
    setNewService({
      serviceType: presetType,
      name: "",
      description: "",
      icon: presetType.includes("cat") ? "🐈" : presetType === "addon" ? "✂️" : presetType.includes("hotel") ? "🏨" : "🐾",
      price: presetType === "addon" ? "100" : presetType.includes("hotel") ? "500" : "600",
      capacity: "4",
      sizes: DEFAULT_DOG_SIZES.map(s => ({ ...s }))
    });
    setShowAddModal(true);
  };

  // Save Add Service Modal
  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.name.trim()) { alert("Please provide a service name."); return; }

    setSubmitting(true);
    try {
      let payload = {
        name: newService.name.trim(),
        description: newService.description.trim(),
        icon: newService.icon || "🐾",
        isActive: true
      };

      if (newService.serviceType === "dog_grooming") {
        payload.type = "grooming"; payload.category = "dog";
        payload.sizes = newService.sizes.map(s => ({ label: s.label.trim(), price: Math.max(0, Number(s.price) || 0) }));
      } else if (newService.serviceType === "cat_grooming") {
        payload.type = "grooming"; payload.category = "cat";
        payload.price = Math.max(0, Number(newService.price) || 0);
      } else if (newService.serviceType === "hotel_dog") {
        payload.type = "hotel"; payload.category = "dog";
        payload.price = Math.max(0, Number(newService.price) || 0);
        payload.capacity = Math.max(1, Number(newService.capacity) || 4);
      } else if (newService.serviceType === "hotel_cat") {
        payload.type = "hotel"; payload.category = "cat";
        payload.price = Math.max(0, Number(newService.price) || 0);
        payload.capacity = Math.max(1, Number(newService.capacity) || 4);
      } else if (newService.serviceType === "addon") {
        payload.type = "addon";
        payload.price = Math.max(0, Number(newService.price) || 0);
      }

      await axios.post("/api/services", payload, { headers: { Authorization: `Bearer ${user?.token}` } });
      setShowAddModal(false);
      showToast(`🎉 "${payload.name}" was successfully added!`);
      fetchServices();
    } catch (err) {
      console.error("Error adding service:", err);
      alert(err.response?.data?.message || "Failed to add service.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = useCallback(async (service) => {
    if (!window.confirm(`Deactivate "${service.name}"?`)) return;
    try {
      await axios.delete(`/api/services/${service._id}`, { headers: { Authorization: `Bearer ${user?.token}` } });
      showToast(`🗑️ "${service.name}" has been deactivated.`);
      fetchServices();
    } catch (err) {
      alert("Failed to delete service");
    }
  }, [user, fetchServices]);

  const handleSeed = async () => {
    if (!window.confirm("This will reset all services to default packages and prices. Continue?")) return;
    setLoading(true);
    try {
      await axios.post("/api/services/seed", {}, { headers: { Authorization: `Bearer ${user?.token}` } });
      showToast("✨ Services reset to default catalog & pricing!");
      fetchServices();
    } catch (err) {
      alert("Failed to seed services");
      setLoading(false);
    }
  };

  // Helper size modifiers for priceForm
  const addPriceFormSize = () => {
    setPriceForm(prev => ({
      ...prev,
      sizes: [...prev.sizes, { label: "Custom Size / Coat", price: 500 }]
    }));
  };

  const removePriceFormSize = (index) => {
    if (priceForm.sizes.length <= 1) {
      alert("Must have at least one size variant.");
      return;
    }
    setPriceForm(prev => ({
      ...prev,
      sizes: prev.sizes.filter((_, i) => i !== index)
    }));
  };

  const adjustPriceFormSizePrice = (index, delta) => {
    setPriceForm(prev => {
      const nextSizes = [...prev.sizes];
      const cur = Number(nextSizes[index].price) || 0;
      nextSizes[index] = { ...nextSizes[index], price: Math.max(0, cur + delta) };
      return { ...prev, sizes: nextSizes };
    });
  };

  // Helper size modifiers for full editForm
  const addFullEditSize = () => {
    setEditForm(prev => ({
      ...prev,
      sizes: [...(prev.sizes || []), { label: "Custom Size / Coat", price: 500 }]
    }));
  };

  const removeFullEditSize = (index) => {
    if (editForm.sizes.length <= 1) {
      alert("Must have at least one size variant.");
      return;
    }
    setEditForm(prev => ({
      ...prev,
      sizes: prev.sizes.filter((_, i) => i !== index)
    }));
  };

  const addCustomDogSize = () => {
    setNewService(prev => ({ ...prev, sizes: [...prev.sizes, { label: "Custom Coat / Size", price: "500" }] }));
  };

  const removeDogSize = (idx) => {
    if (newService.sizes.length <= 1) { alert("Must have at least one size variant."); return; }
    setNewService(prev => ({ ...prev, sizes: prev.sizes.filter((_, i) => i !== idx) }));
  };

  const dogGroomingList = (services.grooming?.dog || []).filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const catGroomingList = (services.grooming?.cat || []).filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const hotelList = (services.hotel || []).filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const addonList = (services.grooming?.addons || []).filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allServicesList = [
    ...(services.grooming?.dog || []),
    ...(services.grooming?.cat || []),
    ...(services.hotel || []),
    ...(services.grooming?.addons || [])
  ].filter(s =>
    (activeTab === "all" ||
     (activeTab === "dog" && s.type === "grooming" && s.category === "dog") ||
     (activeTab === "cat" && s.type === "grooming" && s.category === "cat") ||
     (activeTab === "hotel" && s.type === "hotel") ||
     (activeTab === "addon" && s.type === "addon")) &&
    (s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     s.description?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totalCount =
    (services.grooming?.dog?.length || 0) +
    (services.grooming?.cat?.length || 0) +
    (services.hotel?.length || 0) +
    (services.grooming?.addons?.length || 0);

  if (loading) return <div className="text-center py-20 text-purple-600 font-bold animate-pulse">Loading services &amp; pricing...</div>;

  return (
    <div className="space-y-8">
      {/* Toast */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-[120] bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl font-bold text-sm flex items-center gap-2 border border-gray-700 animate-in slide-in-from-bottom-5">
          {toastMsg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-white p-6 rounded-[2rem] border border-purple-100 shadow-sm">
        <div>
          <h2 className="text-2xl font-black text-gray-800 flex items-center gap-2">
            <span>✂️</span> Service &amp; Price Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Update service pricing, grooming packages, hotel rates, and add-ons in real time. ({totalCount} services)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {/* View switcher */}
          <div className="flex bg-purple-50 p-1 rounded-2xl border border-purple-100">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                viewMode === "grid" ? "bg-purple-600 text-white shadow-xs" : "text-gray-600 hover:text-purple-700"
              }`}
            >
              <GridIcon size={14} color={viewMode === "grid" ? "#FFFFFF" : "#6B7280"} /> Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                viewMode === "table" ? "bg-purple-600 text-white shadow-xs" : "text-gray-600 hover:text-purple-700"
              }`}
            >
              <TableIcon size={14} color={viewMode === "table" ? "#FFFFFF" : "#6B7280"} /> Price Table
            </button>
          </div>

          <button
            onClick={() => {
              const all = [...(services.grooming?.dog || []), ...(services.grooming?.cat || []), ...(services.grooming?.addons || []), ...(services.hotel || [])];
              generateInventoryReport(all);
            }}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-purple-50 text-purple-700 font-bold rounded-2xl hover:bg-purple-100 transition-all border border-purple-200 text-xs"
          >
            📄 PDF Report
          </button>
          <button onClick={handleSeed} className="px-4 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all text-xs">
            🔄 Reset Catalog
          </button>
          <button
            onClick={() => handleOpenAddModal("dog_grooming")}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-2xl shadow-lg hover:scale-[1.02] active:scale-95 transition-all text-xs flex items-center gap-1.5"
          >
            <span className="text-base leading-none">+</span> Add New Service
          </button>
        </div>
      </div>

      {/* Filter & Search */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
        <div className="flex flex-wrap gap-1.5 bg-white p-1.5 rounded-2xl border border-purple-100 shadow-sm w-full sm:w-auto">
          {[
            { id: "all",   label: "All Services",  icon: "🐾", count: totalCount },
            { id: "dog",   label: "Dog Grooming",  icon: "🐶", count: services.grooming?.dog?.length || 0 },
            { id: "cat",   label: "Cat Grooming",  icon: "🐱", count: services.grooming?.cat?.length || 0 },
            { id: "hotel", label: "Pet Hotel",     icon: "🏨", count: services.hotel?.length || 0 },
            { id: "addon", label: "Add-ons",       icon: "✨", count: services.grooming?.addons?.length || 0 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === tab.id ? "bg-purple-600 text-white shadow-md shadow-purple-200" : "text-gray-600 hover:bg-purple-50"
              }`}
            >
              <span>{tab.icon}</span> {tab.label}
              <span className={`text-[10px] px-1.5 rounded-full font-black ${activeTab === tab.id ? "bg-white/25 text-white" : "bg-purple-100 text-purple-700"}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
        <div className="w-full sm:w-64">
          <input
            type="text"
            placeholder="Search services or packages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-purple-100 rounded-2xl text-xs outline-none font-semibold text-gray-800 placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
          />
        </div>
      </div>

      {/* ─── QUICK PRICE EDIT MODAL ─── */}
      {priceModalOpen && priceTargetService && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl border border-purple-100 overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-purple-100 flex justify-between items-center bg-gradient-to-r from-purple-50/80 via-white to-pink-50/50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center text-2xl border border-purple-200 shadow-inner">
                  {renderServiceIcon(priceTargetService.icon, 26)}
                </div>
                <div>
                  <h3 className="text-lg font-black text-gray-800 flex items-center gap-1.5">
                    <span>💰</span> Edit Service Price
                  </h3>
                  <p className="text-xs text-gray-500 font-semibold mt-0.5">
                    {priceTargetService.name} <span className="text-purple-600 font-bold uppercase">({priceTargetService.type})</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPriceModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-gray-100 text-gray-500 font-bold hover:bg-gray-200 flex items-center justify-center text-xs transition-all"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveQuickPrice} className="p-5 sm:p-6 space-y-5">
              {priceForm.sizes.length > 0 ? (
                /* Multi-Size Rates (Dog Grooming Packages) */
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-black text-purple-700 uppercase tracking-wider">
                      🐶 Coat &amp; Size Rates (₱)
                    </label>
                    <button
                      type="button"
                      onClick={addPriceFormSize}
                      className="text-xs font-bold text-purple-600 hover:text-purple-800 bg-purple-50 px-2.5 py-1 rounded-lg border border-purple-200"
                    >
                      + Add Size Tier
                    </button>
                  </div>
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                    {priceForm.sizes.map((sz, idx) => (
                      <div key={idx} className="p-3 bg-purple-50/50 rounded-2xl border border-purple-100 space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={sz.label}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPriceForm(prev => {
                                const next = [...prev.sizes];
                                next[idx] = { ...next[idx], label: val };
                                return { ...prev, sizes: next };
                              });
                            }}
                            className="flex-1 p-2 bg-white border border-purple-100 rounded-xl text-xs font-bold text-gray-800 outline-none focus:border-purple-400"
                            placeholder="Size / Coat Label"
                          />
                          <div className="flex items-center bg-white border border-purple-100 rounded-xl px-3 w-32 focus-within:border-purple-400">
                            <span className="text-xs font-bold text-purple-500 mr-1">₱</span>
                            <input
                              type="number"
                              value={sz.price}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPriceForm(prev => {
                                  const next = [...prev.sizes];
                                  next[idx] = { ...next[idx], price: val === "" ? "" : Number(val) };
                                  return { ...prev, sizes: next };
                                });
                              }}
                              className="w-full p-2 text-sm font-black text-purple-700 outline-none bg-transparent"
                              min="0"
                              step="10"
                            />
                          </div>
                          {priceForm.sizes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePriceFormSize(idx)}
                              className="w-8 h-8 rounded-xl bg-red-50 text-red-500 font-bold hover:bg-red-100 flex items-center justify-center text-xs flex-shrink-0"
                              title="Remove size tier"
                            >
                              ✕
                            </button>
                          )}
                        </div>

                        {/* Quick adjust buttons */}
                        <div className="flex gap-1.5 justify-end">
                          <button
                            type="button"
                            onClick={() => adjustPriceFormSizePrice(idx, -50)}
                            className="px-2 py-0.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-[10px] font-bold hover:bg-gray-50"
                          >
                            -₱50
                          </button>
                          <button
                            type="button"
                            onClick={() => adjustPriceFormSizePrice(idx, 50)}
                            className="px-2 py-0.5 bg-white border border-purple-200 text-purple-700 rounded-lg text-[10px] font-bold hover:bg-purple-50"
                          >
                            +₱50
                          </button>
                          <button
                            type="button"
                            onClick={() => adjustPriceFormSizePrice(idx, 100)}
                            className="px-2 py-0.5 bg-white border border-purple-200 text-purple-700 rounded-lg text-[10px] font-bold hover:bg-purple-50"
                          >
                            +₱100
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Flat-rate price (Cat grooming, Addon, Hotel) */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-gray-700 uppercase tracking-wider">
                      {priceTargetService.type === "hotel" ? "Nightly Rate (₱)" : "Service Price (₱)"}
                    </label>
                    <div className="flex items-center bg-purple-50 border-2 border-purple-200 rounded-2xl px-4 py-1 focus-within:border-purple-500 focus-within:bg-white transition-all shadow-inner">
                      <span className="text-xl font-black text-purple-600 mr-2">₱</span>
                      <input
                        type="number"
                        value={priceForm.price}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, price: e.target.value === "" ? "" : Number(e.target.value) }))}
                        className="w-full py-2.5 text-2xl font-black text-purple-800 outline-none bg-transparent"
                        min="0"
                        step="10"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Preset quick adjustments */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold uppercase text-gray-400 tracking-wider">Quick Adjust:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {[-50, 20, 50, 100, 200].map((delta) => (
                        <button
                          key={delta}
                          type="button"
                          onClick={() => setPriceForm(prev => ({ ...prev, price: Math.max(0, (Number(prev.price) || 0) + delta) }))}
                          className="px-3 py-1.5 bg-purple-50 border border-purple-100 rounded-xl text-xs font-black text-purple-700 hover:bg-purple-100 transition-colors"
                        >
                          {delta > 0 ? `+₱${delta}` : `-₱${Math.abs(delta)}`}
                        </button>
                      ))}
                      {[100, 150, 500, 600, 800].map((exact) => (
                        <button
                          key={`exact-${exact}`}
                          type="button"
                          onClick={() => setPriceForm(prev => ({ ...prev, price: exact }))}
                          className="px-2.5 py-1.5 bg-gray-100 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                          ₱{exact}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-3 border-t border-purple-100 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-black rounded-xl shadow-lg hover:scale-[1.01] active:scale-95 transition-all text-xs disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <CoinsIcon size={14} color="#FFFFFF" />
                  {submitting ? "Saving Prices..." : "Save Price Changes"}
                </button>
                <button
                  type="button"
                  onClick={() => setPriceModalOpen(false)}
                  className="px-5 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl text-xs hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── FULL EDIT SERVICE MODAL ─── */}
      {fullEditModalOpen && editForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl border border-purple-100 max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-purple-100 flex justify-between items-center bg-gradient-to-r from-purple-50/60 via-white to-pink-50/40 flex-shrink-0">
              <div>
                <h3 className="text-lg sm:text-xl font-black text-gray-800 flex items-center gap-2">
                  <span>✏️</span> Edit Service &amp; Pricing
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Modify service details, descriptions, rates, and capacity.</p>
              </div>
              <button
                type="button"
                onClick={() => setFullEditModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-gray-100 text-gray-500 font-bold hover:bg-gray-200 flex items-center justify-center text-xs transition-all"
              >
                ✕
              </button>
            </div>

            {/* Scrollable Form */}
            <form id="full-edit-service-form" onSubmit={handleSaveFullEdit} className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
              {/* Name & Icon */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2 space-y-1">
                  <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Service Name *</label>
                  <input
                    value={editForm.name || ""}
                    onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full p-2.5 bg-purple-50/70 border border-purple-100 rounded-xl outline-none font-bold text-xs text-gray-800 focus:bg-white focus:border-purple-400"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Icon</label>
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center text-lg flex-shrink-0">
                      {renderServiceIcon(editForm.icon, 20)}
                    </span>
                    <select
                      value={editForm.icon || "🐾"}
                      onChange={(e) => setEditForm(f => ({ ...f, icon: e.target.value }))}
                      className="w-full p-2 bg-purple-50/70 border border-purple-100 rounded-xl outline-none font-bold text-xs text-gray-800"
                    >
                      {POPULAR_ICONS.map((ic, i) => <option key={i} value={ic}>{ic} Icon</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Description</label>
                <textarea
                  value={editForm.description || ""}
                  onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full p-2.5 bg-purple-50/70 border border-purple-100 rounded-xl outline-none text-xs text-gray-700 focus:bg-white focus:border-purple-400 resize-none"
                  placeholder="Describe service details..."
                />
              </div>

              {/* Dynamic Pricing Section */}
              {editForm.type === "grooming" && editForm.category === "dog" && editForm.sizes?.length > 0 ? (
                <div className="space-y-2 bg-purple-50/60 p-3.5 rounded-2xl border border-purple-100">
                  <div className="flex justify-between items-center">
                    <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">
                      🐶 Coat &amp; Size Pricing (₱)
                    </label>
                    <button
                      type="button"
                      onClick={addFullEditSize}
                      className="text-xs font-bold text-purple-600 hover:text-purple-800 underline"
                    >
                      + Add Size Tier
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editForm.sizes.map((sz, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={sz.label}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditForm(prev => {
                              const updated = prev.sizes.map((s, i) => i === idx ? { ...s, label: val } : s);
                              return { ...prev, sizes: updated };
                            });
                          }}
                          placeholder="Coat / Size Label"
                          className="flex-1 p-2 bg-white border border-purple-100 rounded-lg text-xs font-bold text-gray-800 outline-none"
                        />
                        <div className="flex items-center bg-white border border-purple-100 rounded-lg px-2 w-28">
                          <span className="text-xs font-bold text-purple-400 mr-1">₱</span>
                          <input
                            type="number"
                            value={sz.price}
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditForm(prev => {
                                const updated = prev.sizes.map((s, i) => i === idx ? { ...s, price: val === "" ? "" : Number(val) } : s);
                                return { ...prev, sizes: updated };
                              });
                            }}
                            placeholder="0"
                            className="w-full p-1.5 text-xs font-black text-purple-700 outline-none"
                            min="0"
                          />
                        </div>
                        {editForm.sizes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFullEditSize(idx)}
                            className="w-7 h-7 rounded-lg bg-red-50 text-red-500 font-bold hover:bg-red-100 flex items-center justify-center text-xs flex-shrink-0"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : editForm.type === "hotel" ? (
                <div className="grid grid-cols-2 gap-2.5 bg-purple-50/60 p-3.5 rounded-2xl border border-purple-100">
                  <div className="space-y-1">
                    <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">Rate per Night (₱)</label>
                    <div className="flex items-center bg-white border border-purple-100 rounded-xl px-2.5 focus-within:border-purple-400 transition-all">
                      <span className="font-bold text-purple-400 mr-1 text-xs">₱</span>
                      <input
                        type="number"
                        value={editForm.price ?? ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value === "" ? "" : Number(e.target.value) }))}
                        className="w-full p-2 font-black text-purple-700 outline-none text-xs bg-transparent"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">Max Capacity</label>
                    <input
                      type="number"
                      value={editForm.capacity ?? 4}
                      onChange={(e) => setEditForm(prev => ({ ...prev, capacity: e.target.value === "" ? "" : Number(e.target.value) }))}
                      className="w-full p-2 bg-white border border-purple-100 rounded-xl font-bold text-gray-800 outline-none text-xs focus:border-purple-400 transition-all"
                      min="1" max="20"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1 bg-purple-50/60 p-3.5 rounded-2xl border border-purple-100">
                  <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">Service Price (₱)</label>
                  <div className="flex items-center bg-white border border-purple-100 rounded-xl px-2.5 focus-within:border-purple-400 transition-all">
                    <span className="font-bold text-purple-400 mr-1 text-xs">₱</span>
                    <input
                      type="number"
                      value={editForm.price ?? ""}
                      onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value === "" ? "" : Number(e.target.value) }))}
                      className="w-full p-2 font-black text-purple-700 outline-none text-xs bg-transparent"
                      min="0"
                    />
                  </div>
                </div>
              )}
            </form>

            {/* Sticky Footer */}
            <div className="p-4 sm:p-5 border-t border-purple-100 bg-gray-50 flex items-center gap-3 flex-shrink-0">
              <button
                type="submit"
                form="full-edit-service-form"
                disabled={submitting}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-black rounded-xl shadow-md hover:scale-[1.01] active:scale-95 transition-all text-xs disabled:opacity-50"
              >
                {submitting ? "Saving Changes..." : "Save All Changes"}
              </button>
              <button
                type="button"
                onClick={() => setFullEditModalOpen(false)}
                className="px-5 py-3 bg-white border border-gray-200 text-gray-600 font-bold rounded-xl text-xs hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ADD NEW SERVICE MODAL ─── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl border border-purple-100 max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Sticky Header */}
            <div className="p-5 sm:p-6 border-b border-purple-100 flex justify-between items-center bg-gradient-to-r from-purple-50/50 via-white to-pink-50/30 flex-shrink-0">
              <div>
                <h3 className="text-lg sm:text-xl font-black text-gray-800 flex items-center gap-2">
                  <span>✨</span> Add New Service
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Create a grooming package, hotel room, or add-on.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 rounded-xl bg-gray-100 text-gray-500 font-bold hover:bg-gray-200 flex items-center justify-center text-xs transition-all"
              >
                ✕
              </button>
            </div>

            {/* Scrollable Form */}
            <form id="add-service-form" onSubmit={handleAddService} className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
              {/* Category Selection */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Service Category</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: "dog_grooming", label: "Dog Grooming",      icon: "🐶" },
                    { id: "cat_grooming", label: "Cat Grooming",      icon: "🐱" },
                    { id: "hotel_dog",    label: "Dog Hotel Room",    icon: "🐕" },
                    { id: "hotel_cat",    label: "Cat Hotel Villa",   icon: "🐈" },
                    { id: "addon",        label: "Add-on Service",    icon: "✨" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setNewService(prev => ({
                        ...prev,
                        serviceType: t.id,
                        icon: t.id.includes("cat") ? "🐈" : t.id === "addon" ? "✂️" : t.id.includes("hotel") ? "🏨" : "🐾",
                        price: t.id === "addon" ? "100" : t.id.includes("hotel") ? "500" : "600"
                      }))}
                      className={`p-2 rounded-xl border text-xs font-bold flex flex-col items-center gap-0.5 transition-all ${
                        newService.serviceType === t.id
                          ? "bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-200 scale-[1.02]"
                          : "bg-purple-50/50 border-purple-100 text-gray-700 hover:bg-purple-100/50"
                      }`}
                    >
                      <span className="text-base">{t.icon}</span>
                      <span className="text-center text-[11px] leading-tight">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name & Icon */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2 space-y-1">
                  <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Service Name *</label>
                  <input
                    placeholder={
                      newService.serviceType === "dog_grooming" ? "e.g., Medicated Spa Grooming" :
                      newService.serviceType === "cat_grooming" ? "e.g., Cat Lion Cut Special" :
                      newService.serviceType.includes("hotel") ? "e.g., Cat Villa Deluxe" : "e.g., Ear Plucking & Cleanse"
                    }
                    value={newService.name}
                    onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full p-2.5 bg-purple-50/70 border border-purple-100 rounded-xl outline-none font-bold text-xs text-gray-800 focus:bg-white focus:border-purple-400"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Icon</label>
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center text-lg flex-shrink-0">
                      {newService.icon}
                    </span>
                    <select
                      value={newService.icon}
                      onChange={(e) => setNewService(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full p-2 bg-purple-50/70 border border-purple-100 rounded-xl outline-none font-bold text-xs text-gray-800"
                    >
                      {POPULAR_ICONS.map((ic, i) => <option key={i} value={ic}>{ic} Icon</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-[11px] font-black text-gray-600 uppercase tracking-wider">Description</label>
                <textarea
                  placeholder="Describe what's included (e.g. shampoo, blow dry, sanitary trim)..."
                  value={newService.description}
                  onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full p-2.5 bg-purple-50/70 border border-purple-100 rounded-xl outline-none text-xs text-gray-700 focus:bg-white focus:border-purple-400 resize-none"
                />
              </div>

              {/* Dynamic Pricing */}
              {newService.serviceType === "dog_grooming" ? (
                <div className="space-y-2 bg-purple-50/60 p-3.5 rounded-2xl border border-purple-100">
                  <div className="flex justify-between items-center">
                    <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">🐶 Coat &amp; Size Pricing (₱)</label>
                    <button type="button" onClick={addCustomDogSize} className="text-xs font-bold text-purple-600 hover:text-purple-800 underline">+ Add Size</button>
                  </div>
                  <div className="space-y-1.5">
                    {newService.sizes.map((sz, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={sz.label}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewService(prev => {
                              const updated = prev.sizes.map((s, i) => i === idx ? { ...s, label: val } : s);
                              return { ...prev, sizes: updated };
                            });
                          }}
                          placeholder="Coat / Size Label"
                          className="flex-1 p-2 bg-white border border-purple-100 rounded-lg text-xs font-bold text-gray-800 outline-none"
                        />
                        <div className="flex items-center bg-white border border-purple-100 rounded-lg px-2 w-28">
                          <span className="text-xs font-bold text-purple-400 mr-1">₱</span>
                          <input
                            type="number"
                            value={sz.price}
                            onChange={(e) => {
                              const val = e.target.value;
                              setNewService(prev => {
                                const updated = prev.sizes.map((s, i) => i === idx ? { ...s, price: val === "" ? "" : Number(val) } : s);
                                return { ...prev, sizes: updated };
                              });
                            }}
                            placeholder="0"
                            className="w-full p-1.5 text-xs font-black text-purple-700 outline-none"
                            min="0"
                          />
                        </div>
                        {newService.sizes.length > 1 && (
                          <button type="button" onClick={() => removeDogSize(idx)} className="w-7 h-7 rounded-lg bg-red-50 text-red-500 font-bold hover:bg-red-100 flex items-center justify-center text-xs flex-shrink-0">✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : newService.serviceType.includes("hotel") ? (
                <div className="grid grid-cols-2 gap-2.5 bg-purple-50/60 p-3.5 rounded-2xl border border-purple-100">
                  <div className="space-y-1">
                    <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">Rate per Night (₱)</label>
                    <div className="flex items-center bg-white border border-purple-100 rounded-xl px-2.5 focus-within:border-purple-400 transition-all">
                      <span className="font-bold text-purple-400 mr-1 text-xs">₱</span>
                      <input
                        type="number"
                        value={newService.price}
                        onChange={(e) => setNewService(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="0"
                        className="w-full p-2 font-black text-purple-700 outline-none text-xs bg-transparent"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">Max Pet Capacity</label>
                    <input
                      type="number"
                      value={newService.capacity}
                      onChange={(e) => setNewService(prev => ({ ...prev, capacity: e.target.value }))}
                      placeholder="4"
                      className="w-full p-2 bg-white border border-purple-100 rounded-xl font-bold text-gray-800 outline-none text-xs focus:border-purple-400 transition-all"
                      min="1" max="20"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1 bg-purple-50/60 p-3.5 rounded-2xl border border-purple-100">
                  <label className="text-[11px] font-black text-purple-700 uppercase tracking-wider">Service Price (₱)</label>
                  <div className="flex items-center bg-white border border-purple-100 rounded-xl px-2.5 focus-within:border-purple-400 transition-all">
                    <span className="font-bold text-purple-400 mr-1 text-xs">₱</span>
                    <input
                      type="number"
                      value={newService.price}
                      onChange={(e) => setNewService(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0"
                      className="w-full p-2 font-black text-purple-700 outline-none text-xs bg-transparent"
                      min="0"
                    />
                  </div>
                </div>
              )}
            </form>

            {/* Sticky Footer */}
            <div className="p-4 sm:p-5 border-t border-purple-100 bg-gray-50 flex items-center gap-3 flex-shrink-0">
              <button
                type="submit"
                form="add-service-form"
                disabled={submitting}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black rounded-xl shadow-md hover:scale-[1.01] active:scale-95 transition-all text-xs disabled:opacity-50"
              >
                {submitting ? "Saving..." : "✨ Create & Activate Service"}
              </button>
              <button type="button" onClick={() => setShowAddModal(false)} className="px-5 py-3 bg-white border border-gray-200 text-gray-600 font-bold rounded-xl text-xs hover:bg-gray-100 transition-all">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── PRICE TABLE VIEW ─── */}
      {viewMode === "table" ? (
        <div className="bg-white rounded-[2rem] border border-purple-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-purple-50/80 border-b border-purple-100 text-gray-700 uppercase font-black tracking-wider text-[11px]">
                  <th className="py-4 px-5">Service</th>
                  <th className="py-4 px-4">Category / Type</th>
                  <th className="py-4 px-4">Current Pricing &amp; Rates</th>
                  <th className="py-4 px-4">Capacity</th>
                  <th className="py-4 px-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-purple-50">
                {allServicesList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-gray-400 italic">
                      No services found matching your search.
                    </td>
                  </tr>
                ) : (
                  allServicesList.map((s) => {
                    const hasSizes = Array.isArray(s.sizes) && s.sizes.length > 0;
                    return (
                      <tr key={s._id} className="hover:bg-purple-50/40 transition-colors">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <span className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center text-lg border border-purple-100 shadow-2xs">
                              {renderServiceIcon(s.icon, 20)}
                            </span>
                            <div>
                              <div className="font-black text-gray-800 text-sm">{s.name}</div>
                              <div className="text-[11px] text-gray-400 line-clamp-1 max-w-xs">{s.description || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col gap-1 items-start">
                            <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              {s.type === "addon" ? "Add-on" : s.type}
                            </span>
                            {s.category && (
                              <span className="text-[10px] font-bold text-gray-500 uppercase">
                                {s.category === "dog" ? "🐶 Dog" : s.category === "cat" ? "🐱 Cat" : s.category}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {hasSizes ? (
                            <div className="flex flex-wrap gap-1.5 max-w-md">
                              {s.sizes.map((sz, idx) => (
                                <span key={idx} className="bg-purple-50 px-2 py-1 rounded-lg border border-purple-100 text-[11px]">
                                  <span className="text-gray-600 font-semibold">{sz.label}: </span>
                                  <span className="font-black text-purple-700">₱{Number(sz.price).toLocaleString()}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm font-black text-purple-700 bg-purple-50 px-2.5 py-1 rounded-xl border border-purple-100">
                              ₱{Number(s.price || 0).toLocaleString()}
                              {s.type === "hotel" && <span className="text-[10px] text-gray-400 font-bold"> / night</span>}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 font-bold text-gray-600">
                          {s.type === "hotel" ? (s.capacity ? `${s.capacity} Pets` : "4 Pets") : "—"}
                        </td>
                        <td className="py-4 px-5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenPriceModal(s)}
                              className="px-3 py-1.5 bg-purple-600 text-white font-bold rounded-xl text-xs hover:bg-purple-700 transition-all flex items-center gap-1 shadow-2xs"
                              title="Edit Price"
                            >
                              <CoinsIcon size={12} color="#FFFFFF" /> Edit Price
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenFullEdit(s)}
                              className="px-2.5 py-1.5 bg-gray-100 text-gray-600 font-bold rounded-xl text-xs hover:bg-gray-200 transition-all"
                              title="Edit Full Service"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(s)}
                              className="px-2.5 py-1.5 bg-red-50 text-red-500 font-bold rounded-xl text-xs hover:bg-red-100 transition-all"
                              title="Deactivate"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ─── GRID CARDS VIEW ─── */
        <div className="space-y-10">
          {/* Dog Grooming */}
          {(activeTab === "all" || activeTab === "dog") && (
            <section className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">
                  <span>🐶</span> Dog Grooming Packages
                  <span className="text-xs font-bold px-2.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">{dogGroomingList.length}</span>
                </h3>
                <button onClick={() => handleOpenAddModal("dog_grooming")} className="text-xs font-bold text-purple-600 hover:text-purple-800 bg-purple-50 px-3 py-1.5 rounded-xl border border-purple-100 transition-all">
                  + Add Dog Package
                </button>
              </div>
              {dogGroomingList.length === 0 ? (
                <p className="text-sm text-gray-400 italic bg-white p-6 rounded-2xl border border-purple-50">No dog grooming packages match your filter.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {dogGroomingList.map(s => (
                    <ServiceCard
                      key={s._id}
                      service={s}
                      onEditPrice={handleOpenPriceModal}
                      onEditFull={handleOpenFullEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Cat Grooming */}
          {(activeTab === "all" || activeTab === "cat") && (
            <section className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">
                  <span>🐱</span> Cat Grooming Services
                  <span className="text-xs font-bold px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">{catGroomingList.length}</span>
                </h3>
                <button onClick={() => handleOpenAddModal("cat_grooming")} className="text-xs font-bold text-orange-600 hover:text-orange-800 bg-orange-50 px-3 py-1.5 rounded-xl border border-orange-100 transition-all">
                  + Add Cat Service
                </button>
              </div>
              {catGroomingList.length === 0 ? (
                <p className="text-sm text-gray-400 italic bg-white p-6 rounded-2xl border border-purple-50">No cat grooming services match your filter.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {catGroomingList.map(s => (
                    <ServiceCard
                      key={s._id}
                      service={s}
                      onEditPrice={handleOpenPriceModal}
                      onEditFull={handleOpenFullEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Pet Hotel */}
          {(activeTab === "all" || activeTab === "hotel") && (
            <section className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">
                  <span>🏨</span> Pet Hotel Rooms &amp; Suites
                  <span className="text-xs font-bold px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">{hotelList.length}</span>
                </h3>
                <button onClick={() => handleOpenAddModal("hotel_dog")} className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-100 transition-all">
                  + Add Hotel Room
                </button>
              </div>
              {hotelList.length === 0 ? (
                <p className="text-sm text-gray-400 italic bg-white p-6 rounded-2xl border border-purple-50">No hotel rooms match your filter.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {hotelList.map(s => (
                    <ServiceCard
                      key={s._id}
                      service={s}
                      onEditPrice={handleOpenPriceModal}
                      onEditFull={handleOpenFullEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Add-ons */}
          {(activeTab === "all" || activeTab === "addon") && (
            <section className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">
                  <span>✨</span> Add-on Services
                  <span className="text-xs font-bold px-2.5 py-0.5 bg-pink-100 text-pink-700 rounded-full">{addonList.length}</span>
                </h3>
                <button onClick={() => handleOpenAddModal("addon")} className="text-xs font-bold text-pink-600 hover:text-pink-800 bg-pink-50 px-3 py-1.5 rounded-xl border border-pink-100 transition-all">
                  + Add Service Add-on
                </button>
              </div>
              {addonList.length === 0 ? (
                <p className="text-sm text-gray-400 italic bg-white p-6 rounded-2xl border border-purple-50">No add-ons match your filter.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {addonList.map(s => (
                    <ServiceCard
                      key={s._id}
                      service={s}
                      onEditPrice={handleOpenPriceModal}
                      onEditFull={handleOpenFullEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Empty State */}
          {totalCount === 0 && (
            <div className="text-center py-20 bg-purple-50 rounded-[3rem] border-2 border-dashed border-purple-200">
              <div className="text-5xl mb-4">📭</div>
              <h3 className="text-xl font-bold text-gray-800">No Services Found</h3>
              <p className="text-gray-500 mb-8">Your database is empty. Load the default catalog to get started.</p>
              <button onClick={handleSeed} className="px-8 py-4 bg-purple-600 text-white font-bold rounded-2xl shadow-xl hover:scale-105 transition-all">
                Load Default Services 🐾
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminServices;
